import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from "@mui/material";

import React, { useState } from "react";

import CustomTextInput from "components/atoms/CustomTextInput";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { GET_PATIENT_RECENT_VISIT } from "utils/reactQueryKeys";
import { useParams } from "react-router-dom";
import { useCustomMutation } from "store/hooks/react-query/mutate/useMutateFunc";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";

import FromOthers from "./FromOthers";
import FromSelf from "./FromSelf";
import { formatAssesmentLog } from "./utils";
import moment from "moment";
import { useAuthCtx } from "store/contextStore/auth/AuthStore";
import CustomButton from "components/atoms/CustomButton";

// Utility to strip HTML for the edit dialog
const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

function AssesmentLog({
  visitId,
  assesmentLogs,
  showCommentField = true,
  handleNext = () => {},
}) {
  const { id } = useParams();
  const authCtx = useAuthCtx();
  const chatContainerRef = React.useRef(null);
  const queryClient = useQueryClient();
  const [comment, setcomment] = React.useState("");

  const [logs, setlogs] = React.useState({ dates: [], data: {} });

  // ── Edit State ──
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLogId, setEditLogId] = useState(null);
  const [editNoteState, setEditNoteState] = useState({
    history: "",
    examination: "",
    findings: "",
    diagnosis: "",
    plan: "",
    comment: "",
  });

  // ── Delete State ──
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLogId, setDeleteLogId] = useState(null);

  React.useEffect(() => {
    if (logs.dates.length) {
      chatContainerRef?.current?.scrollTo({
        top: chatContainerRef?.current?.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [logs]);

  React.useMemo(() => {
    if (assesmentLogs?.length) setlogs(formatAssesmentLog(assesmentLogs));
  }, [assesmentLogs]);

  // ── ADD MUTATION ──
  const { mutate, isLoading } = useCustomMutation(
    {
      url: `/visit/${visitId}`,
      method: "patch",
      data: {
        assessmentLog: [{ note: { comment } }],
      },
    },
    {
      onSuccess: () => {
        setcomment("");
        queryClient.invalidateQueries([GET_PATIENT_RECENT_VISIT, id]);
        toast.success("Success");
      },
      onError: (error) => handleMutateError(error),
    }
  );

  // ── EDIT MUTATION ──
  const { mutate: editMutate, isLoading: isEditing } = useCustomMutation(
    {
      url: `/visit/${visitId}/assessment-log/${editLogId}`,
      method: "patch",
      data: { note: editNoteState },
    },
    {
      onSuccess: () => {
        setEditDialogOpen(false);
        setEditLogId(null);
        setEditNoteState({
          history: "",
          examination: "",
          findings: "",
          diagnosis: "",
          plan: "",
          comment: "",
        });
        queryClient.invalidateQueries([GET_PATIENT_RECENT_VISIT, id]);
        toast.success("Assessment log updated successfully");
      },
      onError: (error) => handleMutateError(error),
    }
  );

  // ── DELETE MUTATION ──
  const { mutate: deleteMutate, isLoading: isDeleting } = useCustomMutation(
    {
      url: `/visit/${visitId}/assessment-log/${deleteLogId}`,
      method: "delete",
    },
    {
      onSuccess: () => {
        setDeleteDialogOpen(false);
        setDeleteLogId(null);
        queryClient.invalidateQueries([GET_PATIENT_RECENT_VISIT, id]);
        toast.success("Assessment log deleted successfully");
      },
      onError: (error) => handleMutateError(error),
    }
  );

  const handleMutateError = (error) => {
    if (Array.isArray(error.message)) {
      return error.message.map((msg) => toast.error(msg));
    }
    return toast.error(error.message);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!comment) return;
    mutate();
  };

  // ── Action Handlers ──
  const openEditDialog = (log) => {
    setEditLogId(log._id);
    setEditNoteState({
      history: stripHtml(log.note?.history),
      examination: stripHtml(log.note?.examination),
      findings: stripHtml(log.note?.findings),
      diagnosis: stripHtml(log.note?.diagnosis),
      plan: stripHtml(log.note?.plan),
      comment: log.note?.comment || "",
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = () => {
    if (
      !editNoteState.comment &&
      !editNoteState.history &&
      !editNoteState.examination &&
      !editNoteState.findings &&
      !editNoteState.diagnosis &&
      !editNoteState.plan
    ) {
      return;
    }
    editMutate();
  };

  const openDeleteDialog = (logId) => {
    setDeleteLogId(logId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    deleteMutate();
  };

  return (
    <Box id={"outer_box_container"}>
      <Box
        sx={{
          width: "100%",
          minHeight: "60vh",
          maxHeight: "100vh",
          p: 1,
          mt: 2,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
        ref={chatContainerRef}
      >
        {logs?.dates?.length ? (
          logs.dates.map((date) => (
            <React.Fragment key={date}>
              <Divider>
                <Chip
                  sx={{ fontSize: "11px" }}
                  label={moment(date).format("MMM Do, YY")}
                  variant="outlined"
                />
              </Divider>

              {logs.data[date].map((item) => (
                <React.Fragment key={item._id}>
                  {item.noteBy._id === authCtx?.state?.user?._id ? (
                    // FIX: Changed flex direction to column to guarantee buttons stay under the bubble
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        width: "100%",
                        mb: 2,
                      }}
                    >
                      <FromSelf item={item} />
                      
                      {/* Buttons safely rendered in a row BELOW the chat bubble */}
                      <Stack direction="row" spacing={1} sx={{ mt: 0.5, mr: 1 }}>
                        <IconButton
                          color="primary"
                          onClick={() => openEditDialog(item)}
                          size="small"
                          sx={{ 
                            bgcolor: 'background.paper', 
                            boxShadow: 1,
                            '&:hover': { bgcolor: 'primary.light', color: 'white' }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => openDeleteDialog(item._id)}
                          size="small"
                          sx={{ 
                            bgcolor: 'background.paper', 
                            boxShadow: 1,
                            '&:hover': { bgcolor: 'error.light', color: 'white' }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Box>
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      <FromOthers item={item} />
                    </Box>
                  )}
                </React.Fragment>
              ))}
            </React.Fragment>
          ))
        ) : (
          <Paper sx={{ p: 2 }}>
            <Typography>No data</Typography>
          </Paper>
        )}
      </Box>

      {showCommentField && (
        <form
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          onSubmit={handleSubmit}
        >
          <CustomTextInput
            boxSx={{ width: "100%" }}
            value={comment}
            name="comment"
            handleChange={(e) => setcomment(e.target.value)}
            placeholder="Type a new message"
          />
          <IconButton
            sx={{ ml: 1 }}
            onClick={handleSubmit}
            disabled={isLoading}
            type="submit"
          >
            <Avatar
              sx={{
                backgroundColor: isLoading
                  ? "background.gray3"
                  : "secondary.main",
              }}
            >
              <SendOutlinedIcon
                sx={{
                  color: isLoading ? "primary.gray" : "white",
                  transform: "rotate(-30deg)",
                }}
              />
            </Avatar>
          </IconButton>
        </form>
      )}

      {showCommentField && (
        <Stack alignItems={"end"} mt={5}>
          <CustomButton
            text={"Next"}
            onClick={handleNext}
            variant="containedBrown"
            sx={{ width: "200px" }}
          />
        </Stack>
      )}

      {/* ── EDIT DIALOG ── */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Assessment Log</DialogTitle>
        <DialogContent>
          {editNoteState.comment ? (
            <TextField
              autoFocus
              margin="dense"
              label="Assessment Note"
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={editNoteState.comment}
              onChange={(e) =>
                setEditNoteState({ ...editNoteState, comment: e.target.value })
              }
            />
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="History"
                fullWidth
                multiline
                rows={2}
                value={editNoteState.history}
                onChange={(e) =>
                  setEditNoteState({
                    ...editNoteState,
                    history: e.target.value,
                  })
                }
              />
              <TextField
                label="Examination"
                fullWidth
                multiline
                rows={2}
                value={editNoteState.examination}
                onChange={(e) =>
                  setEditNoteState({
                    ...editNoteState,
                    examination: e.target.value,
                  })
                }
              />
              <TextField
                label="Findings"
                fullWidth
                multiline
                rows={2}
                value={editNoteState.findings}
                onChange={(e) =>
                  setEditNoteState({
                    ...editNoteState,
                    findings: e.target.value,
                  })
                }
              />
              <TextField
                label="Diagnosis"
                fullWidth
                multiline
                rows={2}
                value={editNoteState.diagnosis}
                onChange={(e) =>
                  setEditNoteState({
                    ...editNoteState,
                    diagnosis: e.target.value,
                  })
                }
              />
              <TextField
                label="Plan"
                fullWidth
                multiline
                rows={2}
                value={editNoteState.plan}
                onChange={(e) =>
                  setEditNoteState({ ...editNoteState, plan: e.target.value })
                }
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            color="primary"
            disabled={isEditing}
          >
            {isEditing ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DELETE DIALOG ── */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this assessment log? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AssesmentLog;